/**
 * @description
 * Cloudflare settings component for marketplace configuration.
 * Manages Cloudflare credentials for worker deployment.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';
import { CloudflareSettingsProps } from './types';

export function CloudflareSettings({ onRefresh }: CloudflareSettingsProps) {
  const { config, updateConfig } = useMarketplaceConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData(e.currentTarget);
      const data = {
        cloudflareAccountId: formData.get('cloudflareAccountId') as string || undefined,
        cloudflareApiToken: formData.get('cloudflareApiToken') as string || undefined,
      };

      await updateConfig(data);
      await onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update Cloudflare settings');
    } finally {
      setLoading(false);
    }
  };

  if (!config) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Cloudflare Configuration</h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Configure Cloudflare credentials for marketplace app deployment and worker management.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-6">
        <div>
          <label htmlFor="cloudflareAccountId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Cloudflare Account ID
          </label>
          <input
            type="text"
            id="cloudflareAccountId"
            name="cloudflareAccountId"
            defaultValue={config.cloudflareAccountId || ''}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="your-cloudflare-account-id"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Your Cloudflare account ID (32 characters). Found in the Cloudflare dashboard.
          </p>
        </div>

        <div>
          <label htmlFor="cloudflareApiToken" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Cloudflare API Token
          </label>
          <input
            type="password"
            id="cloudflareApiToken"
            name="cloudflareApiToken"
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
            placeholder="your-cloudflare-api-token"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            API token with Workers and D1 permissions. Leave empty to keep current token.
          </p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md">
          <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">Required Permissions</h3>
          <ul className="mt-2 text-sm text-blue-700 dark:text-blue-300 list-disc list-inside">
            <li>Workers:Edit</li>
            <li>D1:Edit</li>
            <li>R2:Edit</li>
            <li>Account:Read</li>
          </ul>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Cloudflare Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
